import type { ItemTypeKey } from '../utils/MultiTypeAverageWindow';

export interface RVLayout {
  x: number;
  y: number;
  width: number;
  height: number;
  isMeasured: boolean;
}

export interface RVLayoutInfo {
  index: number;
  dimensions: { width: number; height: number };
}

export interface EngagedRange {
  start: number;
  end: number;
}

export interface LayoutManagerOptions {
  windowSize: { width: number; height: number };
  defaultItemSize?: number;
  averageWindowCapacity?: number;
  getItemType?: (index: number) => ItemTypeKey;
  horizontal?: boolean;
}

export abstract class LayoutManager {
  protected windowSize: { width: number; height: number };
  protected count = 0;
  protected getItemType?: (index: number) => ItemTypeKey;
  protected readonly horizontal: boolean;

  constructor(options: LayoutManagerOptions) {
    this.windowSize = options.windowSize;
    this.getItemType = options.getItemType;
    this.horizontal = options.horizontal ?? false;
  }

  isHorizontal(): boolean {
    return this.horizontal;
  }

  getWindowSize(): { width: number; height: number } {
    return this.windowSize;
  }

  get itemCount(): number {
    return this.count;
  }

  setItemCount(count: number): void {
    this.count = count;
    this.onItemCountChanged();
  }

  setWindowSize(size: { width: number; height: number }): void {
    this.windowSize = size;
    this.onWindowSizeChanged();
  }

  abstract processLayoutInfo(infos: RVLayoutInfo[]): void;
  abstract getLayout(index: number): RVLayout;
  abstract getContentSize(): { width: number; height: number };
  /** No longer required by the tracker; kept on subclasses that find it useful internally. */
  abstract getItemHeight(index: number): number;

  /**
   * Returns the inclusive [start, end] index range whose scroll-axis extents
   * intersect `[start, end]`. Returns null when there are no items. Each
   * manager interprets the range against the active axis (y for vertical,
   * x for horizontal). Linear accumulates per-item primary-axis sizes; grid
   * walks its pre-computed `layouts[]` so multiple items in the same
   * row/column share an extent.
   */
  abstract findIndicesInRange(start: number, end: number): EngagedRange | null;

  /**
   * Re-key the index-based measurement state by the given offset. Items at
   * old index `i` are remapped to `i + offset`; entries that fall outside
   * `[0, count)` are dropped. The engine calls this when the consumer
   * prepends or inserts items so that variable-height measurements follow
   * their owning data rather than the slot they happen to be in. A no-op
   * when `offset` is 0.
   */
  abstract shiftMeasurements(offset: number): void;

  protected onItemCountChanged(): void {}
  protected onWindowSizeChanged(): void {}

  protected typeOf(index: number): ItemTypeKey {
    return this.getItemType ? this.getItemType(index) : 'default';
  }
}
